import type {
  AppliedProductFilters,
  ProductFilterDef,
} from '~/features/experiments/types/product-metadata.types';

/** Platforms the scheduled-batch selection step runs for. */
type SelectionPlatform = 'meta' | 'google';

/** One default filter: the backend keys/columns it may be exposed under, and the value to pre-select. */
interface DefaultSelectionFilter {
  candidates: readonly string[];
  value: string;
  /** Restrict to these platforms; omitted = every platform. */
  platforms?: readonly SelectionPlatform[];
}

/** Backend keys the target-country filter may be exposed under. */
export const COUNTRY_FILTER_KEYS = ['target_country', 'targetCountry', 'country'] as const;
/** Backend keys the content-language filter may be exposed under. */
export const LANGUAGE_FILTER_KEYS = ['content_language', 'contentLanguage', 'language'] as const;

/**
 * Filters pre-applied on the scheduled-batch product-selection step (Google + Meta)
 * so a new batch starts scoped to the US / English / online / in-stock catalog.
 * Candidates are matched case-insensitively against each filter def's `key` and `column`.
 * Meta also defaults `visibility: published` (live on Meta — its closest analogue of
 * `channel: online`); it stays inert until integration exposes `visibility` for Meta.
 */
export const DEFAULT_SELECTION_FILTERS: readonly DefaultSelectionFilter[] = [
  { candidates: COUNTRY_FILTER_KEYS, value: 'US' },
  { candidates: LANGUAGE_FILTER_KEYS, value: 'en' },
  { candidates: ['channel'], value: 'online' },
  { candidates: ['availability'], value: 'in_stock' },
  { candidates: ['visibility'], value: 'published', platforms: ['meta'] },
];

/**
 * Option comparison key: case-insensitive, with spaces / underscores / hyphens treated
 * as one separator — Meta spells availability `in stock` where GMC uses `in_stock`.
 */
const normalizeOption = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

/** First filter def whose `key` or `column` matches one of `candidates` (case-insensitive). */
export function findFilterDef(
  filterDefs: readonly ProductFilterDef[],
  candidates: readonly string[],
): ProductFilterDef | undefined {
  const names = candidates.map((c) => c.toLowerCase());
  return filterDefs.find(
    (d) => names.includes(d.key.toLowerCase()) || names.includes(d.column.toLowerCase()),
  );
}

/**
 * Resolves the default filters against the catalog's filter defs, using the backend's
 * own key and option spelling. Defaults the catalog doesn't expose are skipped so we
 * never send a filter key or value the backend won't recognise.
 */
export function resolveDefaultSelectionFilters(
  filterDefs: readonly ProductFilterDef[],
  platform: SelectionPlatform,
): AppliedProductFilters {
  const applied: AppliedProductFilters = {};

  for (const { candidates, value, platforms } of DEFAULT_SELECTION_FILTERS) {
    if (platforms && !platforms.includes(platform)) continue;
    const def = findFilterDef(filterDefs, candidates);
    if (!def || def.key in applied) continue;

    if (def.type === 'multi_select') {
      const target = normalizeOption(value);
      const option = def.options?.find((o) => normalizeOption(o) === target);
      // A capped (paginated) option list may simply not include the value inline —
      // send the default as-is then; a complete list without it means "not in this catalog".
      if (option !== undefined) applied[def.key] = [option];
      else if (def.paginated) applied[def.key] = [value];
    } else if (def.type === 'text' || def.type === 'text_contains') {
      applied[def.key] = value;
    }
  }

  return applied;
}

/** Values applied under the first key matching `candidates` (case-insensitive), as strings. */
function appliedValuesFor(applied: AppliedProductFilters, candidates: readonly string[]): string[] {
  const names = candidates.map((c) => c.toLowerCase());
  const key = Object.keys(applied).find((k) => names.includes(k.toLowerCase()));
  const raw = key === undefined ? null : applied[key];
  if (raw === null || raw === undefined || raw === '') return [];
  return (Array.isArray(raw) ? raw : [String(raw)]).map((v) => v.trim()).filter(Boolean);
}

/**
 * Builds a locale tag (e.g. `en-US`) from the applied language/country filters, for the
 * suggested run name. Multiple values join with `/`; returns `''` when neither is applied.
 */
export function buildLocaleTag(applied: AppliedProductFilters): string {
  const languages = appliedValuesFor(applied, LANGUAGE_FILTER_KEYS);
  const countries = appliedValuesFor(applied, COUNTRY_FILTER_KEYS);
  return [languages.join('/'), countries.join('/')].filter(Boolean).join('-');
}

/** True when a filter value carries at least one non-blank value (blank-only lists count as empty). */
function hasFilterValue(value: AppliedProductFilters[string] | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.some((v) => v.trim() !== '');
  return typeof value !== 'string' || value.trim() !== '';
}

/**
 * Required scope filters (`ScheduledEnrichmentPlatform.requiredFilters`) the applied map
 * leaves unset, as display labels. Mirrors the backend guard (workflow-service
 * `CurationScopeFilters`) so a run it would 400 is blocked up front — and nothing it would
 * accept is: any candidate key satisfies a group, and a `product_ids` scope satisfies all.
 */
export function missingRequiredScopeFilters(
  required: readonly (readonly string[])[],
  applied: AppliedProductFilters,
): string[] {
  const valueOf = (candidates: readonly string[]) => {
    const names = candidates.map((c) => c.toLowerCase());
    return Object.entries(applied).some(
      ([key, value]) => names.includes(key.toLowerCase()) && hasFilterValue(value),
    );
  };
  if (valueOf(['product_ids'])) return [];
  return required
    .filter((candidates) => !valueOf(candidates))
    .map((candidates) => {
      const label = (candidates[0] ?? '').replace(/_/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    });
}
